/**
 * Dispute circuit breaker card (OCP-580).
 *
 * Renders on AdminBillingProvidersPage. Polls
 * `GET /v1/admin/billing/dispute-breaker` once on mount (+ manual
 * reload). Three visual states:
 *
 *  - tripped (`blocked=true`)  → loud red banner: checkout is BLOCKED,
 *    dispute list underneath, reset button (two-step confirm - the
 *    operator re-opens payments consciously, not by misclick)
 *  - armed, disputes below threshold → compact green "armed" line with
 *    the live count / threshold / window
 *  - dispute read failed (Polar down / not configured) → amber note;
 *    the breaker state itself still renders (the operator must be able
 *    to see + reset the breaker while Polar is unreachable)
 *
 * Red (Tailwind `red-*`) on the tripped state on purpose - this is the
 * "we are losing money / payments are off" banner and must not blend in.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface DisputeRow {
  id: string;
  status: string;
  created_at: string;
  amount_cents: number;
  currency: string;
  order_id: string;
}

interface BreakerState {
  enabled: boolean;
  blocked: boolean;
  threshold: number;
  window_days: number;
  dispute_count: number | null;
  disputes: DisputeRow[];
  dispute_read_error: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  prevented: 'prevented (RDR)',
  early_warning: 'early warning',
  needs_response: 'needs response',
  under_review: 'under review',
  lost: 'lost',
  won: 'won',
};

export function DisputeBreakerCard() {
  const [state, setState] = useState<BreakerState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const s = await api<BreakerState>('/v1/admin/billing/dispute-breaker');
      setState(s);
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Load failed.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function reset() {
    setResetting(true);
    setError(null);
    try {
      await api('/v1/admin/billing/dispute-breaker/reset', { method: 'POST' });
      setConfirming(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Reset failed (HTTP ${err.status}).` : 'Reset failed.');
    } finally {
      setResetting(false);
    }
  }

  if (!state && !error) return null;

  if (error && !state) {
    return (
      <div className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
        Dispute breaker: {error}
      </div>
    );
  }
  if (!state) return null;

  const countLabel =
    state.dispute_count === null ? '?' : String(state.dispute_count);

  return (
    <div className="space-y-2">
      {state.blocked ? (
        <div className="rounded border-2 border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-red-800 dark:text-red-200">
                Dispute breaker TRIPPED - all new checkouts are blocked
              </div>
              <div className="mt-0.5 text-xs text-red-700 dark:text-red-300">
                {countLabel} dispute(s) in the last {state.window_days} days
                (threshold {state.threshold}). Customers get &ldquo;payments
                temporarily unavailable&rdquo;. Review the disputes below, then
                reset to re-open - the next sweep trips again while the count
                stays at/above the threshold, so raise the threshold first if
                the disputes are resolved-but-still-in-window.
              </div>
            </div>
            {confirming ? (
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  disabled={resetting}
                  onClick={() => void reset()}
                  className="rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {resetting ? 'Re-opening…' : 'Yes, re-open payments'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="text-xs text-red-700 dark:text-red-300 hover:underline"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="shrink-0 rounded border border-red-400 dark:border-red-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50"
              >
                Reset breaker…
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm">
          <div className="text-gray-700 dark:text-gray-300">
            <span
              className={`mr-2 rounded px-1.5 py-0.5 text-xs ${
                state.enabled
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }`}
            >
              {state.enabled ? 'armed' : 'sweep disabled'}
            </span>
            Dispute breaker: {countLabel}/{state.threshold} disputes in{' '}
            {state.window_days} days
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="text-xs text-gray-500 hover:underline"
          >
            Refresh
          </button>
        </div>
      )}

      {state.dispute_read_error && (
        <div className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          Live dispute read unavailable ({state.dispute_read_error}) - the
          breaker state above is still authoritative; the count may be stale.
        </div>
      )}

      {state.disputes.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] table-auto border-collapse text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-400">
              <tr>
                <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-1.5">Dispute</th>
                <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-1.5">Status</th>
                <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-1.5">Amount</th>
                <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-1.5">Order</th>
                <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-1.5">Created</th>
              </tr>
            </thead>
            <tbody>
              {state.disputes.map((d) => (
                <tr key={d.id} className="border-b border-gray-100 dark:border-gray-800/60 last:border-b-0">
                  <td className="px-3 py-1.5 font-mono text-gray-600 dark:text-gray-400">{d.id}</td>
                  <td className="px-3 py-1.5">
                    <span
                      className={`rounded px-1.5 py-0.5 ${
                        d.status === 'won'
                          ? 'bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      }`}
                    >
                      {STATUS_LABELS[d.status] ?? d.status}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 tabular-nums">
                    {(d.amount_cents / 100).toFixed(2)} {d.currency.toUpperCase()}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-gray-600 dark:text-gray-400">{d.order_id}</td>
                  <td className="px-3 py-1.5 text-gray-500">
                    {fmtDateTime(d.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
